import React, { useMemo, useState } from "react";
import { openingHours, shopTimeZone } from "../siteData";

/*
  Appointment picker for the service booking page.

  Two deliberate choices worth keeping.

  1. The shop clock, not the visitor's clock. Alvarado is America/Chicago. A
     customer browsing from another timezone must not be shown a Monday that has
     already started, or a 5pm slot that is 7pm at the shop. Every "today" and
     "now" below is resolved through Intl in shopTimeZone.

  2. These are REQUESTED times, not confirmed ones. This site has no live view of
     the bay schedule, so it must not render a slot as available when nobody has
     checked. The copy says requested throughout and the confirmation comes from
     a person. Showing a state we have not earned is how a customer turns up to
     an appointment that was never on the board.

  Dates are handled as plain YYYY-MM-DD strings. Nothing here constructs a local
  Date from user input, which is what makes daylight saving a non-event.
*/

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const SLOT_MINUTES = 30;
// No appointment starts inside the last hour of the day; there has to be time to
// look at the vehicle and talk to the owner before the doors close.
const CLOSING_BUFFER = 60;
// Same-day requests need enough lead time for someone to see them.
const SAME_DAY_LEAD = 120;
const BOOKING_HORIZON_DAYS = 90;

const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parse = (s) => s.split("-").map(Number);

function shopToday() {
  // en-CA formats as YYYY-MM-DD, which is the shape we compare on.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: shopTimeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

function shopNowMinutes() {
  const [h, m] = new Intl.DateTimeFormat("en-GB", {
    timeZone: shopTimeZone, hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date()).split(":").map(Number);
  return h * 60 + m;
}

function addDays(dateStr, days) {
  const [y, m, d] = parse(dateStr);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return ymd(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
}

function weekday(dateStr) {
  const [y, m, d] = parse(dateStr);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function slotsFor(dateStr, today, nowMinutes) {
  const hours = openingHours[weekday(dateStr)];
  if (!hours) return [];
  const [oh, om] = hours[0].split(":").map(Number);
  const [ch, cm] = hours[1].split(":").map(Number);
  const earliest = dateStr === today ? nowMinutes + SAME_DAY_LEAD : 0;
  const out = [];
  for (let t = oh * 60 + om; t <= ch * 60 + cm - CLOSING_BUFFER; t += SLOT_MINUTES) {
    if (t >= earliest) out.push(t);
  }
  return out;
}

function clockLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h < 12 ? "AM" : "PM";
  return `${((h + 11) % 12) + 1}:${pad(m)} ${suffix}`;
}

export function longDateLabel(dateStr) {
  const [y, m, d] = parse(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export default function AppointmentPicker({ value, onChange, error }) {
  const today = useMemo(shopToday, []);
  const nowMinutes = useMemo(shopNowMinutes, []);
  const lastDay = useMemo(() => addDays(today, BOOKING_HORIZON_DAYS), [today]);

  const [ty, tm] = parse(today);
  const [cursor, setCursor] = useState({ year: ty, month: tm - 1 });

  const [ly, lm] = parse(lastDay);
  const atFirstMonth = cursor.year === ty && cursor.month === tm - 1;
  const atLastMonth = cursor.year === ly && cursor.month === lm - 1;

  const grid = useMemo(() => {
    const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
    const days = new Date(Date.UTC(cursor.year, cursor.month + 1, 0)).getUTCDate();
    const cells = Array.from({ length: first.getUTCDay() }, () => null);
    for (let d = 1; d <= days; d += 1) {
      const date = ymd(cursor.year, cursor.month, d);
      const open = openingHours[weekday(date)] !== null;
      const inRange = date >= today && date <= lastDay;
      cells.push({
        date,
        day: d,
        isToday: date === today,
        // A day is selectable only if the shop opens and at least one slot is
        // still ahead of the shop clock. A Saturday afternoon at 3:30pm has no
        // bookable slot left, and the grid should say so rather than accept it.
        selectable: open && inRange && slotsFor(date, today, nowMinutes).length > 0,
      });
    }
    return cells;
  }, [cursor, today, lastDay, nowMinutes]);

  const slots = value.date ? slotsFor(value.date, today, nowMinutes) : [];

  function move(delta) {
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  }

  return (
    <div className={`appt-picker${error ? " has-error" : ""}`}>
      <div className="appt-calendar">
        <div className="appt-month">
          <button type="button" onClick={() => move(-1)} disabled={atFirstMonth} aria-label="Previous month">‹</button>
          <p aria-live="polite">{MONTHS[cursor.month]} {cursor.year}</p>
          <button type="button" onClick={() => move(1)} disabled={atLastMonth} aria-label="Next month">›</button>
        </div>

        <div className="appt-daynames" aria-hidden="true">
          {DAY_LABELS.map((d, i) => <span key={i}>{d}</span>)}
        </div>

        <div className="appt-grid" role="group" aria-label="Choose an appointment date">
          {grid.map((cell, i) => cell === null ? <span key={`x${i}`} /> : (
            <button
              key={cell.date}
              type="button"
              className={`appt-day${value.date === cell.date ? " selected" : ""}${cell.isToday ? " today" : ""}`}
              disabled={!cell.selectable}
              aria-pressed={value.date === cell.date}
              aria-label={longDateLabel(cell.date)}
              onClick={() => onChange({ date: cell.date, time: "" })}
            >
              {cell.day}
            </button>
          ))}
        </div>

        <p className="appt-legend">
          Closed Sundays. Mon to Fri 9:00 AM to 6:00 PM, Sat 10:00 AM to 4:00 PM.
        </p>
      </div>

      <div className="appt-times">
        {value.date ? (
          <>
            <p className="appt-times-head">{longDateLabel(value.date)}</p>
            <div className="appt-slots" role="group" aria-label="Choose a time">
              {slots.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`appt-slot${value.time === clockLabel(t) ? " selected" : ""}`}
                  aria-pressed={value.time === clockLabel(t)}
                  onClick={() => onChange({ date: value.date, time: clockLabel(t) })}
                >
                  {clockLabel(t)}
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="appt-empty">Pick a date and the available times appear here.</p>
        )}
      </div>

      <p className="appt-note">
        {value.date && value.time
          ? <><b>Requesting {longDateLabel(value.date)} at {value.time}.</b> We confirm your appointment by phone or email before it is on the board.</>
          : "Times are requested, not confirmed. Someone at the shop confirms your slot by phone or email."}
      </p>

      {error ? <p className="appt-error" role="alert">{error}</p> : null}

      {/* The picker is buttons, so the chosen values reach FormData through these. */}
      <input type="hidden" name="preferredDate" value={value.date ? longDateLabel(value.date) : ""} />
      <input type="hidden" name="preferredTime" value={value.time} />
    </div>
  );
}
